import { Divider, LinearProgress, Stack, useTheme } from "@mui/material";
import type { FC } from "react";
import { DashboardPortfolioList } from "web_myplann/Dashboard/Components/DashboardPortfolioList";
import { DashboardPropertyList } from "web_myplann/Dashboard/Components/DashboardPropertyList";
import type { DashboardType } from "web_myplann/Dashboard/Types/DashboardType";
import type {
	ClientPropertyItemResponseDTO,
	PortfolioListItemResponseDTO,
	PortfolioListReportingSummaryResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { EmptyListSkeleton } from "web_myplann/Shared/Components/EmptyListSkeleton";

interface DashboardDetailContentProps {
	portfolios: PortfolioListItemResponseDTO[] | undefined;
	properties: ClientPropertyItemResponseDTO[] | undefined;
	reportingData: PortfolioListReportingSummaryResponseDTO[] | undefined;
	isLoading: boolean;
	dashboard: DashboardType;
}

export const DashboardDetailContent: FC<DashboardDetailContentProps> = ({
	portfolios,
	properties,
	reportingData,
	isLoading,
	dashboard,
}) => {
	const { t } = useResource();
	const { spacingTokens } = useTheme();

	const hasPortfolios = portfolios && portfolios.length > 0;
	const hasProperties = properties && properties.length > 0;
	const isDataLoaded = portfolios !== undefined && properties !== undefined;
	const isEmpty =
		!isLoading && isDataLoaded && !hasPortfolios && !hasProperties;

	return (
		<Stack pt={spacingTokens.lg} position="relative" flex={1}>
			{isLoading && (
				<LinearProgress overlay sx={{ color: dashboard.gradientTo }} />
			)}
			<Stack
				flex={isEmpty ? 1 : undefined}
				sx={{ opacity: isLoading ? 0.6 : 1, transition: "opacity 0.3s ease" }}
			>
				{isEmpty && (
					<EmptyListSkeleton
						label={t(Resources.Dashboard.EmptyList.label)}
						description={t(Resources.Dashboard.EmptyList.description)}
						height="100%"
						flex={1}
						skeletonSize="lg"
					/>
				)}

				{hasPortfolios && (
					<DashboardPortfolioList
						portfolios={portfolios}
						reportingData={reportingData}
					/>
				)}

				{hasPortfolios && hasProperties && <Divider variant="hairline" />}

				{hasProperties && <DashboardPropertyList properties={properties} />}
			</Stack>
		</Stack>
	);
};
